import {idiomaActual, localeActual} from '../i18n/idioma';

// Formato de numeros y fechas para pantalla en el idioma activo: espanol de
// Colombia (1.234,56 y 30/07/2026) o ingles (1,234.56 y 30 Jul 2026, sin la
// ambiguedad de mes y dia).
const formateadores = new Map<string, Intl.NumberFormat>();

export function numero(valor: number | string | null | undefined, decimales = 0): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  const n = typeof valor === 'number' ? valor : Number(valor);
  if (!Number.isFinite(n)) return String(valor);
  const clave = `${localeActual()}|${decimales}`;
  let formateador = formateadores.get(clave);
  if (!formateador) {
    formateador = new Intl.NumberFormat(localeActual(), {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
    formateadores.set(clave, formateador);
  }
  return formateador.format(n);
}

// Dolares sin decimales: "USD 1.234.567"; vacio, "—".
export const usd = (valor: number | null | undefined) =>
  valor === null || valor === undefined ? '—' : `USD ${numero(valor)}`;

const MESES_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MESES_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Hindi (9/10/2026): los nombres cortos de los meses del idioma.
const MESES_HI = Array.from({length: 12}, (_, i) =>
  new Intl.DateTimeFormat('hi-IN', {month: 'short', timeZone: 'UTC'}).format(new Date(Date.UTC(2026, i, 1))),
);
const nombreDeMes = (indice: number) => ({en: MESES_EN, hi: MESES_HI, es: MESES_ES})[idiomaActual()][indice];

// "2026-07-30" -> "30/07/2026" (es) o "30 Jul 2026" (en). Otros textos se devuelven tal cual.
export function fecha(valor: string | number | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  const texto = String(valor);
  const partes = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!partes) return texto;
  if (idiomaActual() === 'en')
    return `${Number(partes[3])} ${nombreDeMes(Number(partes[2]) - 1) ?? partes[2]} ${partes[1]}`;
  return `${partes[3]}/${partes[2]}/${partes[1]}`;
}

// "2026-07-01" o "2026-07" -> "jul 2026" (es) o "Jul 2026" (en).
export function mes(valor: string | null | undefined): string {
  if (!valor) return '—';
  const partes = valor.match(/^(\d{4})-(\d{2})/);
  if (!partes) return valor;
  return `${nombreDeMes(Number(partes[2]) - 1) ?? partes[2]} ${partes[1]}`;
}

// Fecha y hora de un instante (ISO) en el formato del idioma activo, sin
// segundos y en 24 horas ("07/10/2026 14:05"): con "a. m." un punto al final de
// la frase quedaba doble y los segundos alargaban las tablas (recorrido 7/10/2026).
export function fechaHora(valor: string | Date | null | undefined): string {
  if (!valor) return '—';
  const instante = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(instante.getTime())) return String(valor);
  return instante
    .toLocaleString(localeActual(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
    .replace(',', '');
}

// Solo la fecha de un instante (ISO), en el formato del idioma activo.
export function fechaDeInstante(valor: string | Date | null | undefined): string {
  if (!valor) return '—';
  const instante = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(instante.getTime())) return String(valor);
  return instante.toLocaleDateString(localeActual());
}

export function textoOGuion(valor: string | number | boolean | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (typeof valor === 'boolean')
    return ({es: valor ? 'Sí' : 'No', en: valor ? 'Yes' : 'No', hi: valor ? 'हाँ' : 'नहीं'} as const)[idiomaActual()];
  return String(valor);
}
